/**
 * The sender of every email Teslyn sends (decision 890, after 508 and 553): the report emails to clients, Email…,
 * the alerts and the sign-in emails all come from `no-reply@teslyn.au`, named after the organisation the email is
 * for: "No Reply Thomson Airborne powered by Teslyn". When the organisation is not known (a sign-in link for someone
 * in two organisations, or in none yet) the name is "No Reply via Teslyn", the name Supabase Auth's own sender has.
 *
 * It imports nothing, so the Edge Functions use the same rule (tools/dev/copy-edge-shared.mjs builds it with
 * `app-email`, `alert-notices` and `auth-email`).
 */

/** The address every Teslyn email comes from (decision 553). */
export const APP_EMAIL_ADDRESS = 'no-reply@teslyn.au';
/** The sender when the organisation is not known (decisions 508, 890). */
export const APP_EMAIL_FROM = `No Reply via Teslyn <${APP_EMAIL_ADDRESS}>`;
/** The longest organisation name the sender carries; a longer one is shortened with "…". */
export const SENDER_ORGANISATION_MAX = 60;

/**
 * The organisation's name as a sender's name can carry it: on one line, without the characters that end or break an
 * address's display name (quotes, backslashes, angle brackets, control characters), spaces collapsed. '' when
 * nothing is left.
 */
export function senderOrganisationName(name: string | null | undefined): string {
  const clean = (name ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f"\\<>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean.length <= SENDER_ORGANISATION_MAX) return clean;
  return `${clean.slice(0, SENDER_ORGANISATION_MAX - 1).trimEnd()}…`;
}

/** The sender's name: "No Reply Thomson Airborne powered by Teslyn", or "No Reply via Teslyn". */
export function appEmailSenderName(organisation: string | null | undefined): string {
  const org = senderOrganisationName(organisation);
  return org ? `No Reply ${org} powered by Teslyn` : 'No Reply via Teslyn';
}

/**
 * The From of an email for the organisation (decision 890):
 * `"No Reply Thomson Airborne powered by Teslyn" <no-reply@teslyn.au>`, quoted so a comma or full stop in the name
 * cannot split the address; `No Reply via Teslyn <no-reply@teslyn.au>` without an organisation.
 */
export function appEmailFrom(organisation: string | null | undefined): string {
  const org = senderOrganisationName(organisation);
  return org ? `"${appEmailSenderName(org)}" <${APP_EMAIL_ADDRESS}>` : APP_EMAIL_FROM;
}
